import { Checkbox, Grid } from '@solinkcorp/solink-components';
import classNames from 'classnames';
import { type EventCamera } from 'clients/PlatformApi';
import S3Image from 'components/S3Image/S3Image';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import { type VideoDiscoverySite } from 'utils/types';

type CameraState = EventCamera & { checked: boolean };

const initialCamerasState = (cameras: EventCamera[]) => {
  const modifiedCameras = cameras.map((camera) => {
    return { ...camera, checked: true };
  });
  return modifiedCameras;
};

interface ThumbnailsProps {
  location: VideoDiscoverySite;
  eventCameras: EventCamera[];
}

const Thumbnails: React.FC<ThumbnailsProps> = ({ location, eventCameras }) => {
  const locale = useLocale();
  const [cameras, setCameras] = React.useState<CameraState[]>([]);

  const checkedThumbnails = cameras.filter((camera) => camera.checked);

  const checkCamera = (id: string) => {
    const newCameraState = cameras.map((camera) => {
      const chCameraId = findCameraCallhomeId(location, camera);
      if (chCameraId === id) {
        return { ...camera, checked: !camera.checked };
      }
      return camera;
    });
    setCameras(newCameraState);
  };

  const thumbnailsClass = classNames('thumbnails', {
    'two-columns': cameras.length > 1,
    'two-columns-print': checkedThumbnails.length > 1,
  });

  React.useEffect(() => {
    setCameras(initialCamerasState(eventCameras));
  }, [eventCameras]);

  return (
    <Grid
      gridTemplateRows="none"
      gridColumnGap="1.5em"
      gridRowGap="0.75em"
      alignItems="end"
      className={thumbnailsClass}
    >
      {cameras.map((camera) => {
        const { checked, name, thumbnail } = camera;
        const cameraClass = classNames('camera', { 'not-checked': !checked });
        const chCameraId = findCameraCallhomeId(location, camera);
        return chCameraId ? (
          <div key={chCameraId} className={cameraClass}>
            <label className="camera-name">{name}</label>
            <S3Image id={chCameraId} url={thumbnail} />
            <Checkbox
              name={chCameraId}
              checked={checked}
              className="checkbox"
              labelText={locale.print.includeImage}
              onChange={() => checkCamera(chCameraId)}
            />
          </div>
        ) : null;
      })}
    </Grid>
  );
};

function findCameraCallhomeId(location: VideoDiscoverySite, eventCamera: EventCamera) {
  if (eventCamera.chId) {
    return eventCamera.chId;
  }
  return location.cameras.find((locationCamera) => locationCamera.cameraId === eventCamera.id)?.id;
}

export default Thumbnails;
